import { format as dateFnsFormat, isValid, parseISO } from 'date-fns';
import { getDateFnsLocale } from '../../i18n/dateFnsLocale';
import {
    dateFnsMediumPattern,
    dateFnsShortPattern,
    resolveDateFormat,
    type DateFormatOrder,
    type DateFormatPreference,
} from './preferences';

export type DateDisplayStyle = 'short' | 'medium' | 'long';

export type DateFormatPrefs = {
    uiLanguage?: string | null;
    dateFormat?: DateFormatPreference;
    /** IANA timezone for datetime display (optional). */
    timeZone?: string | null;
};

function toDate(value: string | Date | null | undefined): Date | null {
    if (value == null || value === '') return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    const s = String(value).trim();
    if (!s) return null;
    // Date-only YYYY-MM-DD: parse as local calendar day to avoid UTC shift in display
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [y, m, d] = s.split('-').map(Number);
        const local = new Date(y!, m! - 1, d!);
        return Number.isNaN(local.getTime()) ? null : local;
    }
    const iso = parseISO(s);
    if (isValid(iso)) return iso;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
}

function intlLocale(uiLanguage?: string | null): string {
    return String(uiLanguage || 'en-US').trim() || 'en-US';
}

function orderFromPrefs(prefs?: DateFormatPrefs | null): DateFormatOrder {
    return resolveDateFormat(prefs?.uiLanguage, prefs?.dateFormat);
}

/**
 * Format a stored ISO / Date / yyyy-MM-dd for display.
 * - `short` — numeric order (MM/dd/yyyy, dd/MM/yyyy, …)
 * - `medium` — compact abbreviated month (Jul 30 / 30 jul); omits year when current year
 * - `long` — full month name via Intl
 */
export function formatDateDisplay(
    value: string | Date | null | undefined,
    prefs?: DateFormatPrefs | null,
    style: DateDisplayStyle = 'medium',
    empty = '—',
): string {
    const d = toDate(value);
    if (!d) return empty;

    const lang = intlLocale(prefs?.uiLanguage);
    const order = orderFromPrefs(prefs);
    const dateFnsLocale = getDateFnsLocale(lang);

    if (style === 'short') {
        return dateFnsFormat(d, dateFnsShortPattern(order), { locale: dateFnsLocale });
    }

    if (style === 'medium') {
        const includeYear = d.getFullYear() !== new Date().getFullYear();
        return dateFnsFormat(d, dateFnsMediumPattern(order, includeYear), {
            locale: dateFnsLocale,
        });
    }

    // long
    try {
        return new Intl.DateTimeFormat(lang, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            ...(prefs?.timeZone ? { timeZone: prefs.timeZone } : {}),
        }).format(d);
    } catch {
        return new Intl.DateTimeFormat(lang, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        }).format(d);
    }
}

export function formatDateTimeDisplay(
    value: string | Date | null | undefined,
    prefs?: DateFormatPrefs | null,
    empty = '—',
): string {
    const d = toDate(value);
    if (!d) return empty;
    const lang = intlLocale(prefs?.uiLanguage);
    const order = orderFromPrefs(prefs);
    const dateFnsLocale = getDateFnsLocale(lang);
    const includeYear = d.getFullYear() !== new Date().getFullYear();
    const datePart = dateFnsFormat(d, dateFnsMediumPattern(order, includeYear), {
        locale: dateFnsLocale,
    });
    const timePart = dateFnsFormat(d, 'h:mm a', { locale: dateFnsLocale });
    return `${datePart}, ${timePart}`;
}

/**
 * Legacy long-date helper used across admin tables.
 * Prefer formatDateDisplay(..., prefs, 'long') when prefs are available.
 */
export function formatDateLongEnUs(dateString: string): string {
    if (!dateString) return 'N/A';
    return formatDateDisplay(dateString, { uiLanguage: 'en-US', dateFormat: 'MDY' }, 'long', 'N/A');
}
